import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { useMediaQuery } from '@mui/material';
import { cn } from '@natter-co/design-system';

import { BarIn, History } from './Waveform.styled';

const BAR = 3;
const GAP = 3;
const TICK_MS = 70;
const FLOOR = 0.12;
// The static bars shown instead of the scrolling ones under prefers-reduced-motion.
const SHAPE = [0.45, 0.7, 1, 0.8, 0.95, 0.6, 0.85, 0.5, 0.35];
const STATIC_MAX = 24;

type Props = { active: boolean; level: number; className?: string };

/**
 * A scrolling history of the voice level, the full width of its parent. It starts empty; a bar enters on the right
 * every 70ms at the current `level` and grows in, so the line builds leftward as the person talks and dips in their
 * pauses, and older bars fade out under the left-edge mask. Bars are sized by height with fully round ends (at least
 * 3px, so silence shows as round dots). A short static cluster on the right under reduced motion.
 * Decorative only (aria-hidden); the recording state is announced through the live region in OpenTextQuestion.
 *
 * DS gap: the design system has no voice waveform. Ported from `natalie/Waveform.tsx` in the post-event-feedback
 * prototype.
 */
export const Waveform = ({ active, level, className }: Props) => {
  // A JS-driven animation cannot be stopped by a CSS variant alone.
  const reduced = useMediaQuery('(prefers-reduced-motion: reduce)', { noSsr: true });
  const ref = useRef<HTMLSpanElement>(null);
  const [count, setCount] = useState(0);
  const [bars, setBars] = useState<{ id: number; v: number }[]>([]);
  const nextId = useRef(0);
  const levelRef = useRef(level);
  levelRef.current = level;

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const fit = () => setCount(Math.max(4, Math.floor((el.clientWidth + GAP) / (BAR + GAP))));
    fit();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(fit);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!active || reduced) return undefined;
    const id = window.setInterval(() => {
      const l = levelRef.current;
      // A little jitter so a held note still reads as a voice; quiet readings draw short bars, not dots.
      const v = l < 0.05 ? FLOOR : Math.max(FLOOR, Math.min(1, l * (0.85 + Math.random() * 0.3)));
      setBars((xs) => [...xs.slice(-(count - 1)), { id: nextId.current++, v }]);
    }, TICK_MS);
    return () => window.clearInterval(id);
  }, [active, count, reduced]);

  const shown = reduced
    ? Array.from({ length: Math.min(count, STATIC_MAX) }, (_, i) => ({
        id: i,
        v: 0.25 + 0.5 * SHAPE[i % SHAPE.length],
      }))
    : bars;

  return (
    <History
      ref={ref}
      aria-hidden
      data-testid={reduced ? 'waveform-static' : undefined}
      className={cn(
        'flex h-6 min-w-0 flex-1 items-center justify-end overflow-hidden text-[var(--foreground-accent)]',
        className
      )}
      style={{ gap: GAP }}
    >
      {shown.map((bar) => (
        // Height, not scaleY: scaling a pill squashes its round caps flat, so short bars looked square.
        <span
          key={bar.id}
          className="flex shrink-0 items-center"
          style={{ width: BAR, height: `max(${BAR}px, ${bar.v * 100}%)` }}
        >
          {reduced ? (
            <span className="h-full w-full rounded-full bg-current" />
          ) : (
            <BarIn className="h-full w-full rounded-full bg-current" />
          )}
        </span>
      ))}
    </History>
  );
};
